import { ITooltipParams } from 'ag-grid-enterprise';
import { APIComponentAttributes } from '@ardoq/api-types';
import { cellRendererPropsOperations } from './CellRenderers/cellRendererPropsOperations';
import { InventoryColumnDef, InventoryContext } from './types';
import { columnOperations } from './columns/columnOperations';
import { inventoryDatasourceOperations } from './datasourceOperations';
import { t } from '@ardoq/i18n';

export const tooltipValueGetter = (
  params: ITooltipParams<APIComponentAttributes, unknown, InventoryContext>
) => {
  // AG Grid external-lib callback: locale is propagated via context mutation
  // from Inventory.tsx; read fresh on each invocation.
  const i18nLocale = params.context?.i18nLocale ?? {};

  // Field-less extra columns (computed/actions) have no field applicability, so
  // skip the field-oriented tooltip messages entirely. A consumer can still opt
  // in via `columnTooltip` (headerTooltip) or its own getter.
  if ((params.colDef as InventoryColumnDef)?.context?.isExtraColumn) {
    return undefined;
  }

  if (!cellRendererPropsOperations.isComponentColumnApplicable(params)) {
    return t(
      i18nLocale,
      'inventoryApp:ag.tooltip.notApplicable',
      'This value isn’t applicable for the selected component type.'
    );
  }
  if (
    cellRendererPropsOperations.requiresAnotherEntityChangeForUnpersistedComponent(
      params
    )
  ) {
    return t(
      i18nLocale,
      'inventoryApp:ag.tooltip.setComponentNameFirst',
      'Enter a component name first to edit this field.'
    );
  }
  if (cellRendererPropsOperations.isReadOnlyField(params)) {
    return t(
      i18nLocale,
      'inventoryApp:ag.tooltip.readOnly',
      'This isn’t an editable field.'
    );
  }
  if (cellRendererPropsOperations.isConfidentialFieldUserCannotSee(params)) {
    return t(
      i18nLocale,
      'inventoryApp:ag.tooltip.confidentialNoAccess',
      'You don’t have access to view this confidential field.'
    );
  }
  if (
    !cellRendererPropsOperations.hasEditPermissionForComponent(params) &&
    !columnOperations.canEditIncomingReferenceColumn(params)
  ) {
    return t(
      i18nLocale,
      'inventoryApp:ag.tooltip.noEditPermission',
      'You don’t have permission to edit this value. Contact your admin to request access.'
    );
  }
  if (cellRendererPropsOperations.isDerivedField(params)) {
    return t(
      i18nLocale,
      'inventoryApp:ag.tooltip.derivedField',
      'To edit this reactive field, go to Preferences > Metamodel > Fields.'
    );
  }

  if (cellRendererPropsOperations.isCalculatedField(params)) {
    const isReportDatasource =
      inventoryDatasourceOperations.isReportInventoryDatasourceSelection(
        params.context.datasourceSelection
      );

    if (isReportDatasource) {
      return t(
        i18nLocale,
        'inventoryApp:ag.tooltip.calculatedField',
        'To edit this calculated field, go to Preferences > Metamodel > Fields.'
      );
    }

    return t(
      i18nLocale,
      'inventoryApp:ag.tooltip.calculatedFieldRecalculate',
      'This is a calculated field that isn’t editable. You can recalculate this field in the column header.'
    );
  }

  if (cellRendererPropsOperations.isLockedComponent(params)) {
    return t(
      i18nLocale,
      'inventoryApp:ag.tooltip.lockedComponent',
      'Unlock this component to edit this value.'
    );
  }
  if (cellRendererPropsOperations.isExternallyManagedFieldOnComponent(params)) {
    return t(
      i18nLocale,
      'inventoryApp:ag.tooltip.externallyManaged',
      'This data is managed externally and editing is restricted'
    );
  }
};
